import { ListScreen, Notice } from '../components/chrome/list-screen'
import { Table, TBody, Th, THead } from '../components/ui/table'
import { Blank } from '../components/ui/panel'
import { Button } from '../components/ui/button'
import { BY_ID, type Route } from '../app/nav'

/**
 * The five screens still without a working surface.
 *
 * They are **built**, not stubbed: the real page header, the real toolbar, the real view tabs, the
 * real column set, and the table card. What they do not do is invent rows. A console that shows
 * forty-two fictional mailboxes teaches an operator a layout and then lies about the state of their
 * system, and the first time they act on one of those numbers the product has cost them something.
 *
 * So each one says, in the place where rows would be, exactly which endpoint is missing and which
 * section of `architecture.md` specifies it. When the endpoint lands, the only change is swapping
 * the empty state for a query — the columns, the toolbar and the states are already right.
 */

interface ScreenSpec {
  subtitle: string
  tabs: Array<{ id: string; label: string }>
  columns: Array<{ label: string; width?: number; align?: 'left' | 'center' | 'right' }>
  primary?: string
  /** The route the API would expose. */
  endpoint: string
  /** What it will hold, in one sentence an operator would recognise. */
  holds: string
}

export const SPECS: Partial<Record<Route, ScreenSpec>> = {
  sequences: {
    subtitle: 'The steps a contact walks through, and the wait between them.',
    tabs: [{ id: 'all', label: 'All' }],
    columns: [
      { label: 'Sequence' },
      { label: 'Steps', width: 100, align: 'right' },
      { label: 'Used by', width: 140, align: 'right' },
      { label: 'Updated', width: 130 },
    ],
    primary: 'New sequence',
    endpoint: 'GET /api/sequences',
    holds: 'Sequencing is §22. Nothing schedules a step yet because nothing dispatches one.',
  },
  warmup: {
    subtitle: 'A new mailbox ramps before it sends volume. The ramp is the difference between arriving and being filtered.',
    tabs: [{ id: 'active', label: 'Active' }],
    columns: [
      { label: 'Mailbox' },
      { label: 'Day', width: 100, align: 'right' },
      { label: 'Target', width: 120, align: 'right' },
      { label: 'Sent today', width: 130, align: 'right' },
      { label: 'Status', width: 150 },
    ],
    endpoint: 'GET /api/warmup',
    holds: 'Warmup ramp is §33 and depends on mailboxes existing first.',
  },
  alerts: {
    subtitle: 'What the system decided you needed to know without being asked.',
    tabs: [
      { id: 'open', label: 'Open' },
      { id: 'resolved', label: 'Resolved' },
    ],
    columns: [
      { label: 'Alert' },
      { label: 'Severity', width: 130 },
      { label: 'Source', width: 180 },
      { label: 'Raised', width: 140 },
    ],
    endpoint: 'GET /api/alerts',
    holds: 'Alerting is §91. Nothing sends, so nothing crosses a threshold.',
  },
  webhooks: {
    subtitle:
      'Callbacks arriving from providers and from recipients — not subscriptions this system sends. §50\u2019s gateway terminates the provider, unsubscribe and tracking paths.',
    tabs: [
      { id: 'deliveries', label: 'Deliveries' },
      { id: 'unprocessed', label: 'Unprocessed' },
    ],
    columns: [
      { label: 'Source' },
      { label: 'Status', width: 140 },
      { label: 'Received', width: 180 },
      { label: 'Processed', width: 180 },
    ],
    endpoint: 'GET /api/webhooks — exists, platform plane only',
    holds:
      'The read is built, but it is Axmion\u2019s rather than a customer\u2019s: a delivery arrives before anything is known about it, so webhook_events has no organization column and reading it needs platform_view_platform_audit. The console has no platform-plane surface yet, which is what this screen is waiting for \u2014 not the endpoint. §50 gateway, §51 idempotency.',
  },
}

export function UnwiredScreen({ route }: { route: Route }) {
  const entry = BY_ID[route]
  const spec = SPECS[route]

  if (!spec) return null

  return (
    <ListScreen
      title={entry.label}
      subtitle={spec.subtitle}
      tabs={spec.tabs}
      activeTab={spec.tabs[0]?.id}
      savedViews={[]}
      query=""
      onQuery={() => {}}
      primary={
        spec.primary ? (
          <Button variant="primary" disabled title="No endpoint yet">
            {spec.primary}
          </Button>
        ) : undefined
      }
      notice={
        <Notice tone="info" label="Not wired">
          This screen is built. Its endpoint is not: <code className="font-mono">{spec.endpoint}</code>{' '}
          does not exist yet
          {entry.section ? ` (${entry.section})` : ''}. Nothing below is invented — the table is
          empty because there is nothing to read, not because the screen is unfinished.
        </Notice>
      }
    >
      <Table>
        <THead>
          {spec.columns.map((column) => (
            <Th key={column.label} width={column.width} align={column.align}>
              {column.label}
            </Th>
          ))}
        </THead>
        <TBody />
      </Table>

      <Blank
        icon="unwired"
        title={`No ${entry.label.toLowerCase()} to show`}
        body={spec.holds}
        action={
          <Button disabled title="No endpoint yet">
            Nothing to load
          </Button>
        }
      />
    </ListScreen>
  )
}
